import { useReducer, useEffect, useCallback, useContext, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, Redirect } from 'react-router-dom'
import { t } from 'ttag'
import PropTypes from 'prop-types'

import { Globals } from '@h2/globals'
import { api, errors } from '@h2/api'

import { Skeleton } from './panels/skeleton'
import { Email } from './panels/email'
import { BirthDate } from './panels/birthDate'
import { EmailWarning } from './panels/emailWarning'
import { Username } from './panels/username'
import { ExternalId } from './panels/externalId'
import { AccountType } from './panels/accountType'
import { NavigatorType } from './panels/navigatorType'
import { ExplorerType } from './panels/explorerType'
import { About } from './panels/about'
import { EducatorType } from './panels/educatorType'
import { CounselorType } from './panels/counselorType'
import { K12EducatorType } from './panels/k12EducatorType'
import { K12Program } from './panels/k12Program'
import { K12Population } from './panels/k12Population'
import { Organization } from './panels/organization'
import { ParentApproval } from './panels/parentApproval'
import { Pending } from './join/pending'
import { Student } from './panels/student'
import { Join } from './join'
import { Finished } from './join/finished'

import { SignInForm } from '../signInForm'
import { usePageTitle } from '../hooks/usePageTitle'
import { unauthorizedUserAuthenticated } from '../../actions/unauthorizedUserAuthenticated'
import { authorizationCode } from '../../selectors/authorizationCode'

const panelMap = {
  login: SignInForm,
  birthDate: BirthDate,
  email: Email,
  emailRequired: Email,
  emailWarning: EmailWarning,
  username: Username,
  externalId: ExternalId,
  mainType: AccountType,
  basic: About,
  explorerType: ExplorerType,
  navigatorType: NavigatorType,
  educatorType: EducatorType,
  counselorType: CounselorType,
  k12Type: K12EducatorType,
  k12Population: K12Population,
  k12Program: K12Program,
  studentInfo: Student,
  parentApproval: ParentApproval,
  organization: Organization,
  pending: Pending,
  complete: Finished,
  membership: Join,
}
const reducer = (state, updates) => ({
  ...state,
  ...updates,
  properties: { ...state.properties, ...updates.properties }
})
const initialState = {
  panel: '',
  properties: {},
  formErrors: null,
  serverErrorMsg: ''
}
const Registration = ({ inDialog, customDetourHandler, reset = false }) => {
  const dispatchRedux = useDispatch()
  const { user, isNavianceUser } = useContext(Globals)
  const authzCode = useSelector(authorizationCode)
  const isUserInSessionOnMount = useRef(Boolean(user))
  const { state: locationState } = useLocation()
  const [{ panel, properties, serverErrorMsg, formErrors }, dispatch] = useReducer(
    reducer,
    initialState
  )
  const panelRef = useRef('')
  const { mutate: init } = useMutation(
    () => api.fetch('/auth/register/init', { method: 'POST' }),
    {
      onSuccess: data => {
        dispatch({
          serverErrorMsg: '',
          panel: data.panel,
          properties: data.properties
        })
      }
    }
  )
  const { mutate: validate, isLoading: isValidating } = useMutation(
    form =>
      api.fetch('/auth/register/validate', {
        method: 'POST',
        body: JSON.stringify({
          panel: /^email/.test(panel) ? 'email' : panel,
          properties: { ...properties, ...form }
        })
      }),
    {
      onSuccess: data => {
        if (data.auth?.membership?.created) {
          dispatchRedux(
            unauthorizedUserAuthenticated(data.auth.membership.authorizationCode)
          )
        }

        dispatch({
          formErrors: null,
          serverErrorMsg: '',
          panel: data.panel,
          properties: data.properties
        })
      },
      onError: err => {
        const validation = errors.getValidation(err)

        if (validation) {
          dispatch({ formErrors: validation })
        } else {
          dispatch({ serverErrorMsg: t`An error occured` })
        }
      }
    }
  )
  const renderPanel = useCallback(
    (name, props) => {
      dispatch({
        panel: name,
        properties: props
      })
    },
    [dispatch]
  )

  usePageTitle(t`Registration`, inDialog)
  useEffect(() => {
    panelRef.current = panel
  }, [panel])

  useEffect(() => {
    if (reset || (!user && panelRef.current !== 'login')) {
      init()
    }
  }, [init, reset, user])

  useEffect(() => {
    if (locationState?.panel) {
      dispatch({ panel: locationState.panel })
    }
  }, [locationState, dispatch])

  if (isUserInSessionOnMount.current && !isNavianceUser) {
    // User is already signed in
    return <Redirect to="/" />
  }

  if (user && !authzCode && !isNavianceUser) {
    // User signed in before completing registration
    return <Redirect to="/" />
  }

  if (panel) {
    const Panel = panelMap[panel]

    return (
      <Panel
        inDialog={inDialog}
        validate={validate}
        properties={properties}
        isValidating={isValidating}
        renderPanel={renderPanel}
        formErrors={formErrors}
        serverErrorMsg={serverErrorMsg}
        customDetourHandler={customDetourHandler}
      />
    )
  }

  return <Skeleton inDialog={inDialog} />
}

Registration.propTypes = {
  reset: PropTypes.bool,
  inDialog: PropTypes.bool,
  customDetourHandler: PropTypes.bool
}

export { Registration }
